/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { yupResolver } from '@hookform/resolvers/yup';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { shallowEqual } from 'react-redux';
import { useUnmount } from 'react-use';
import { ObjectSchema } from 'yup';

import { isEnvironmentReadOnly } from '@ibm-watson/shared/modules/ia/store/access-control/selectors';
import { useAppSelector, useAppDispatch } from '@ibm-watson/shared/store';
import { loadCatalogItem } from '../store/catalog/actions';
import { selectCatalogItemById } from '../store/catalog/selectors';
import {
  loadCurrentIntegration,
  resetCurrentIntegration,
  setDraftIntegrationEnableSecretFields
} from '../store/channels/actions';
import {
  loadCurrentIntegration as loadCurrentClassicIntegration,
  resetDraftIntegrationForm
} from '../store/channels/classic/actions';
import { selectIsIntegrationResetNeeded } from '../store/channels/classic/selectors';
import { updateDraftIntegrationSettings } from '../store/channels/reducer';
import {
  areIntegrationsLoading,
  selectDraftIntegration,
  selectIntegrationById,
  selectIsIntegrationInitializing,
  selectIsIntegrationPrematurelyCreated
} from '../store/channels/selectors';
import {
  CATALOG_CHANNEL_TYPE,
  SEGMENT_EXTENSION_TYPE,
  channelTypeToChannelName,
  channelTypeToChannelDescription,
  WEBCHAT_CHANNEL_TYPE,
  PHONE_CHANNEL_TYPE,
  INTERCOM_CHANNEL_TYPE
} from '../util/channelTypes';
import { DEFAULT_FORM_DEBOUNCE_DELAY } from '../util/constants';
import { flattenWatchErrors, objectDifference } from '../util/helpers';
import { useCatalogItemHelper } from './useCatalogItemHelper';
import { useChannelHelpers } from './useChannelHelpers';

// For developers: change this variable to true if you want to see
// console logs every time an input field gets changed.
const DEBUG_UPDATE_DRAFT_INTEGRATION = false;

const NON_QUICK_SETUP_CHANNELS = [
  WEBCHAT_CHANNEL_TYPE,
  PHONE_CHANNEL_TYPE,
  INTERCOM_CHANNEL_TYPE
];

/**
 * Instatiate a channel integration form with appropriate
 * hooks attached.
 *
 * Should be called 1 time by parent form component that uses
 * TabbedHeader and FormProvider
 *
 */
export const useChannelForm = (
  channelType: string,
  assistantId: string,
  environmentId: string | undefined,
  integrationId: string,
  settingsSchema: ObjectSchema<any>,
  catalogItemId?: string,
  isSegment?: boolean
) => {
  // Use one useForm & schema...
  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: settingsSchema.cast({}),
    resolver: yupResolver(settingsSchema)
  });
  const {
    control,
    reset,
    trigger,
    formState: { errors }
  } = formMethods;
  const watchedFields = useWatch({ control });

  const dispatch = useAppDispatch();
  const draftIntegration = useAppSelector(selectDraftIntegration, shallowEqual);
  const savedIntegration = useAppSelector(
    state => selectIntegrationById(state, draftIntegration?.integration_id),
    shallowEqual
  );
  const isIntegrationInitializing = useAppSelector(
    selectIsIntegrationInitializing
  );
  const isIntegrationPrematurelyCreated = useAppSelector(
    selectIsIntegrationPrematurelyCreated
  );
  const catalogItem = useAppSelector(state =>
    selectCatalogItemById(state, catalogItemId)
  );
  const loading = useAppSelector(areIntegrationsLoading);
  const isResetNeeded = useAppSelector(selectIsIntegrationResetNeeded);
  const isReadOnly = useAppSelector(state =>
    isEnvironmentReadOnly(state, environmentId)
  );

  const [initialName, setInitialName] = useState<string>('');
  const [hasPopulated, setHasPopulated] = useState<boolean>(false);
  /**
   * Conditions on QuickSetup:
   * -  QuickSetup is disabled for Intercom, Phone and Web Chat
   * -  If there is no integration ID, that means an integration
   *    has not been created yet, and QuickSetup should be
   *    enabled.
   * -  If the integration is prematurely created, that means
   *    we are still in QuickSetup. Integrations are prematurely
   *    created when we enter the "subscribe step" or "webhook creation step"
   *    because in order to subscribe, we need an integration id so we create
   *    the integration & integration ID here, but we have not yet finished
   *    the QuickSetup.
   */
  const [useQuickSetup, setUseQuickSetup] = useState<boolean>(
    !NON_QUICK_SETUP_CHANNELS.includes(channelType) &&
      (_.isNil(integrationId) || Boolean(isIntegrationPrematurelyCreated))
  );

  const { setNewIntegration, updateIntegrationName } =
    useChannelHelpers(formMethods);
  const { parseSpecification, getCatalogFieldsValid } = useCatalogItemHelper(
    formMethods,
    catalogItemId
  );

  /**
   * Reset the draft when unmounting the component.
   */
  useUnmount(async () => {
    reset();
    dispatch(resetCurrentIntegration());
  });

  /**
   * If no integration ID exists, load a blank integration into
   * store state.
   */
  useEffect(() => {
    if (_.isNil(integrationId)) {
      const safeChannelType = isSegment ? SEGMENT_EXTENSION_TYPE : channelType;
      const name = !_.isEmpty(catalogItem)
        ? catalogItem?.title
        : channelTypeToChannelName[safeChannelType];
      const description = !_.isEmpty(catalogItem)
        ? catalogItem?.description
        : channelTypeToChannelDescription[safeChannelType];

      const settings =
        safeChannelType === CATALOG_CHANNEL_TYPE
          ? parseSpecification(catalogItem)
          : JSON.parse(JSON.stringify(settingsSchema.cast({})));

      // For non-segment catalog items, `parseSpecification` is used
      // convert to an integration and copy the catalog item ID to
      // the integration settings. Segment integration is far more
      // simpler than a standard catalog item, and so we do not use
      // `parseSpecification`. But that also means we must manually
      // move the catalog item ID to the integration settings.
      if (isSegment) {
        settings.catalog_item_id = catalogItemId;
      }

      setNewIntegration({
        name,
        description,
        type: channelType, // segment's type here is CATALOG_CHANNEL_TYPE
        settings
      });
    }
  }, [
    catalogItem,
    channelType,
    settingsSchema,
    integrationId,
    isSegment,
    parseSpecification,
    setNewIntegration,
    catalogItemId
  ]);

  /**
   * If the integration ID exists and/or changes, load it.
   * Also, if the integration settings contains a catalog item ID,
   * load it as a catalog item.
   */
  useEffect(() => {
    if (!_.isNil(integrationId) && !isIntegrationPrematurelyCreated) {
      if (!_.isNil(environmentId)) {
        dispatch(
          loadCurrentIntegration({
            assistantId,
            environmentId,
            integrationId
          })
        );
      } else {
        dispatch(
          loadCurrentClassicIntegration({
            assistantId,
            integrationId
          })
        );
      }

      if (!_.isNil(catalogItemId)) {
        dispatch(
          loadCatalogItem({
            assistantId,
            catalogItemId
          })
        );
      }
    }
  }, [
    assistantId,
    catalogItemId,
    dispatch,
    environmentId,
    integrationId,
    isIntegrationPrematurelyCreated
  ]);

  /**
   * When `isResetNeeded` is true, this means that the draft
   * integration has been updated, and the React form is out of date.
   * Therefore, we reset and re-trigger the form, rebasing it to the
   * more up-to-date draft integration settings. Finally, we set
   * `reset_integration_form` to false.
   */
  useEffect(() => {
    if (isResetNeeded) {
      reset(settingsSchema.cast(_.cloneDeep(draftIntegration?.settings)));
      trigger();
      dispatch(resetDraftIntegrationForm(false));
    }
  }, [
    dispatch,
    draftIntegration,
    isResetNeeded,
    reset,
    settingsSchema,
    trigger
  ]);

  /**
   * If and when the draft integration changes, OR if any ui forms
   * change any watched fields, first check if the draft integration
   * and watched fields differ. If so, sync the draft integration
   * with watched fields. Update the dirty and valid states as well.
   */
  useEffect(() => {
    const handler = setTimeout(() => {
      const settings = JSON.parse(JSON.stringify(watchedFields));
      if (
        // Do not parse the draft integration if the integration
        // is still being processed. Since this variable is a
        // dependency, this useEffect will re-trigger the moment
        // this is true.
        !isIntegrationInitializing &&
        // If `isResetNeeded` is true, then draft integration is more
        // up-to-date than the React form and we don't want to
        // overwrite the draft integration settings.
        !isResetNeeded &&
        !_.isNil(draftIntegration) &&
        // We need to compare settings for integrations in QuickSetup and
        // we need to compare settingsWithoutSecrets for integrations in edit mode
        (!_.isEqual(settings, draftIntegration?.settings) ||
          (!_.isEmpty(errors) && draftIntegration?.settings?.valid))
        // sometimes react-hook-form doesn't have "errors" properly populated first time thru so this catches that
      ) {
        // We use a manual definition of dirtiness rather than the
        // react-hook-form definition, as we want to track literal
        // differences between watched fields and the integration
        // settings.
        // In quick setup, we're comparing to an empty settings.
        // In non-quick setup, we're comparing to saved settings.
        const dirtyFields: any = useQuickSetup
          ? objectDifference(watchedFields, settingsSchema.cast({}))
          : objectDifference(watchedFields, savedIntegration?.settings);

        const settingsIsDirty = !_.isEmpty(dirtyFields);

        // This piece of code is left intentionally commented for a
        // future feature. As of today, whenever someone revisits an
        // existing integration, (s)he will have to reinput ALL
        // private/secret fields in order to save again. The
        // consequence of this is, tooling must (1) determine the
        // validity of the settings WITHOUT considering missing
        // fields in private/secrets, and (2) private/secrets MUST
        // be parsed so that missing fields must be removed instead
        // of being an empty string or null - otherwise the existing
        // field will be unintentionally overwritten.
        // So, when we decide to implement "partial update", aka when
        // we allow for Tanyas to update only one/a subset of
        // private/secret fields, please uncomment settingsIsValid and
        // parsedSettings, and then use these variables in
        // updateDraftIntegrationSettings.
        //
        // const settingsIsValid = useQuickSetup
        //   ? _.isEmpty(errors)
        //   : _.isEmpty(parseNonQuickSetupErrors(errors));
        // const parsedSettings = useQuickSetup
        //   ? settings
        //   : parseNonQuickSetupSettings(settings);

        if (DEBUG_UPDATE_DRAFT_INTEGRATION) {
          console.log('useChannelForm updateDraftIntegrationSettings:', {
            settingsIsValid: _.isEmpty(errors),
            settingsIsDirty,
            errors: {
              errors,
              flatten: flattenWatchErrors(errors)
            },
            settings: {
              watch: settings,
              casted: settingsSchema.cast({}),
              draft: draftIntegration?.settings,
              saved: savedIntegration?.settings
            },
            dirtyFields
          });
        }

        dispatch(
          updateDraftIntegrationSettings({
            settings: settings,
            // When channel type is catalog (but not segment), check
            // for the validity using getCatalogFieldsValid since it
            // has private fields which are derived from public
            // fields and for nonQuickSetup flows secret fields can
            // be null or undefined hence it fails to check it using
            // yup. For others channel types just check if there are
            // no errors present.
            isValid:
              channelType === CATALOG_CHANNEL_TYPE && !isSegment
                ? getCatalogFieldsValid()
                : _.isEmpty(errors),
            isDirty: settingsIsDirty
          })
        );
      }
    }, DEFAULT_FORM_DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [
    channelType,
    dispatch,
    draftIntegration,
    environmentId,
    errors,
    getCatalogFieldsValid,
    isIntegrationInitializing,
    isIntegrationPrematurelyCreated,
    isResetNeeded,
    isSegment,
    savedIntegration,
    settingsSchema,
    useQuickSetup,
    watchedFields
  ]);

  /**
   * After the form updates the draft integration's settings (see
   * previous useEffect), we ask the form to re-trigger
   * validation. This will reveal any errors that might occur in the
   * integration form.
   */
  useEffect(() => {
    if (!loading && !_.isNil(draftIntegration) && hasPopulated) {
      trigger();
    }
  }, [loading, draftIntegration, trigger, hasPopulated]);

  /**
   * Onload, we ask the form to reset and re-trigger
   * validation. This will reveal any errors that might occur in the
   * integration form.
   */
  useEffect(() => {
    if (!loading && !_.isNil(draftIntegration) && !hasPopulated) {
      setHasPopulated(true);
      setInitialName(draftIntegration.name);
      // Conditions on QuickSetup:
      // - QuickSetup is disabled for Intercom, Phone and Web Chat
      // - If there is no integration ID, that means an integration
      //   has not been created yet, and QuickSetup should be
      //   enabled.
      // - If the integration is prematurely created, that means
      //   we are still in QuickSetup.
      const isQuickSetup =
        !NON_QUICK_SETUP_CHANNELS.includes(channelType) &&
        (_.isNil(draftIntegration?.integration_id) ||
          Boolean(isIntegrationPrematurelyCreated));
      setUseQuickSetup(isQuickSetup);
      dispatch(setDraftIntegrationEnableSecretFields(isQuickSetup));
      reset(settingsSchema.cast(_.cloneDeep(draftIntegration?.settings)));
      trigger();
    }
  }, [
    channelType,
    dispatch,
    draftIntegration,
    hasPopulated,
    isIntegrationPrematurelyCreated,
    useQuickSetup,
    loading,
    reset,
    settingsSchema,
    trigger
  ]);

  /**
   * Update QuickSetup status if integration_id is set.
   */
  useEffect(() => {
    if (!loading && !_.isNil(draftIntegration)) {
      // Conditions on QuickSetup:
      // - QuickSetup is disabled for Intercom, Phone and Web Chat
      // - If there is no integration ID, that means an integration
      //   has not been created yet, and QuickSetup should be
      //   enabled.
      // - If the integration is prematurely created, that means
      //   we are still in QuickSetup.
      const isQuickSetup =
        !NON_QUICK_SETUP_CHANNELS.includes(channelType) &&
        (_.isNil(draftIntegration?.integration_id) ||
          Boolean(isIntegrationPrematurelyCreated));
      setUseQuickSetup(isQuickSetup);
    }
  }, [
    channelType,
    dispatch,
    draftIntegration,
    isIntegrationPrematurelyCreated,
    loading
  ]);

  return {
    loading,
    initialName,
    updateIntegrationName,
    formMethods,
    useQuickSetup,
    isReadOnly,
    hasPopulated
  };
};
